import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { megabytes, toBytes, updateSource, METHODS, type TSource } from '../../entities/source';

type TMethod = (typeof METHODS)[number];

export interface ISourceDraft {
  name: string;
  onAuthFailure: TSource['onAuthFailure'];
  eventTypeFrom: TSource['eventTypeFrom'];
  eventTypePath: string;
  maxBodyMb: string;
  allowedMethods: TMethod[];
  responseStatus: string;
  responseBody: string;
  responseContentType: string;
  customerResolutionMode: TSource['customerResolutionMode'];
  customerFieldFrom: NonNullable<TSource['customerFieldFrom']>;
  customerFieldPath: string;
}

const isMethod = (value: string): value is TMethod =>
  (METHODS as readonly string[]).includes(value);

const draftOf = (source: TSource): ISourceDraft => ({
  name: source.name,
  onAuthFailure: source.onAuthFailure,
  eventTypeFrom: source.eventTypeFrom,
  eventTypePath: source.eventTypePath ?? '',
  maxBodyMb: megabytes(source.maxBodyBytes).replace(' MB', ''),
  allowedMethods: source.allowedMethods.filter(isMethod),
  responseStatus: String(source.responseStatus),
  responseBody: source.responseBody ?? '',
  responseContentType: source.responseContentType ?? '',
  customerResolutionMode: source.customerResolutionMode,
  customerFieldFrom: source.customerFieldFrom ?? 'body',
  customerFieldPath: source.customerFieldPath ?? '',
});

// Which part of the page a changed field belongs to — the names the Save button lists.
const SECTION_OF: Record<keyof ISourceDraft, string> = {
  name: 'Name',
  onAuthFailure: 'When a check fails',
  eventTypeFrom: 'Event type',
  eventTypePath: 'Event type',
  maxBodyMb: 'What we accept',
  allowedMethods: 'What we accept',
  responseStatus: 'What we answer',
  responseBody: 'What we answer',
  responseContentType: 'What we answer',
  customerResolutionMode: 'Customer',
  customerFieldFrom: 'Customer',
  customerFieldPath: 'Customer',
};

const parsesAsJson = (text: string): boolean => {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
};

export const useSourceSettings = (source: TSource) => {
  const client = useQueryClient();
  const saved = useMemo(() => draftOf(source), [source]);
  const [draft, setDraft] = useState<ISourceDraft>(saved);
  const [edited, setEdited] = useState(false);

  // The server's copy wins until somebody types, so a refetch cannot overwrite a half-filled field.
  const current = edited ? draft : saved;

  const change = useCallback((patch: Partial<ISourceDraft>) => {
    setDraft((previous) => ({ ...previous, ...patch }));
    setEdited(true);
  }, []);

  const body = current.responseBody.trim();
  // A custom body with no type named goes out as JSON when it is JSON, as text otherwise.
  const contentType =
    current.responseContentType ||
    (body.length > 0 ? (parsesAsJson(body) ? 'application/json' : null) : null);

  const save = useMutation({
    mutationFn: () =>
      updateSource(source.id, {
        name: current.name.trim(),
        onAuthFailure: current.onAuthFailure,
        eventTypeFrom: current.eventTypeFrom,
        eventTypePath: current.eventTypePath.trim() || null,
        maxBodyBytes: toBytes(Number(current.maxBodyMb)),
        allowedMethods: current.allowedMethods,
        responseStatus: Number(current.responseStatus),
        responseBody: body || null,
        responseContentType: contentType,
        customerResolutionMode: current.customerResolutionMode,
        ...(current.customerResolutionMode === 'by_field'
          ? {
              customerFieldFrom: current.customerFieldFrom,
              customerFieldPath: current.customerFieldPath.trim(),
            }
          : {}),
      }),
    onSuccess: () => {
      setEdited(false);
      void client.invalidateQueries({ queryKey: ['source', source.id] });
      void client.invalidateQueries({ queryKey: ['sources'] });
    },
  });

  const changed = (Object.keys(saved) as (keyof ISourceDraft)[]).filter(
    (key) => edited && JSON.stringify(current[key]) !== JSON.stringify(saved[key]),
  );
  const sections = [...new Set(changed.map((key) => SECTION_OF[key]))];

  const named = current.name.trim().length > 0;
  const bodyMb = Number(current.maxBodyMb);
  const ceilingMb = source.maxBodyBytesCeiling / 1_048_576;
  const status = Number(current.responseStatus);

  const problems = {
    methods: current.allowedMethods.length === 0,
    body: !Number.isFinite(bodyMb) || bodyMb <= 0 || toBytes(bodyMb) > source.maxBodyBytesCeiling,
    status: !Number.isInteger(status) || status < 200 || status > 599,
    customerField:
      current.customerResolutionMode === 'by_field' && current.customerFieldPath.trim() === '',
  };
  const valid =
    named && !problems.methods && !problems.body && !problems.status && !problems.customerField;

  return {
    draft: current,
    change,
    dirty: sections.length > 0,
    sections,
    named,
    problems,
    ceilingMb,
    contentType,
    canSave: sections.length > 0 && valid,
    save,
    source,
  };
};

export type TSourceSettings = ReturnType<typeof useSourceSettings>;
